import { useMemo } from 'react'
import { Modal, ModalProps, Form, Select, Input } from 'antd'

import {
  ZoneSuspensionReason,
  ZoneSuspensionInfoInput,
} from '~/gql/generated/graphql'
import { capitalize } from '~/utils'

export type SuspendModalProps = ModalProps & {
  // Number of bays affected, used in title
  count?: number
  onConfirm: (info: ZoneSuspensionInfoInput) => void
}

// Collect a suspension reason and notes
export const SuspendModal = ({
  open,
  onCancel,
  onConfirm,
  count,
}: SuspendModalProps) => {
  const [form] = Form.useForm()

  const reasonOptions = useMemo(
    () =>
      Object.entries(ZoneSuspensionReason)
        .filter(([key]) => key !== 'unknown')
        .map(([key, value]) => ({
          value,
          label: capitalize(key.replace(/_/g, ' ')),
        })),
    [],
  )

  const reasonChosen = Form.useWatch('reason', form)

  const handleOk = async () => {
    const { reason, notes } = await form.validateFields()
    onConfirm({ reason, notes: notes ?? '' })
    form.resetFields()
  }

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    onCancel?.(e)
    form.resetFields()
  }

  return (
    <Modal
      // 1050? lets modal appear above "actions" dropdown
      zIndex={1050}
      open={open}
      title={`Suspend ${count ?? ''} bay${count === 1 ? '' : 's'}?`}
      okButtonProps={{ disabled: !reasonChosen }}
      onOk={handleOk}
      onCancel={handleCancel}
      destroyOnClose
    >
      <Form layout="vertical" form={form} name="suspendModalForm">
        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ required: true, message: 'Please choose a reason' }]}
          tooltip="The reason these bays are being suspended"
        >
          <Select options={reasonOptions} />
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notes"
          tooltip="Additional info about this suspension"
        >
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  )
}
